import { Component, HostBinding, OnInit } from '@angular/core';
import { TranslateService } from './i18n/translate.service';
import { InBrowserModelService } from './services/in-browser-model.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'Bompadre';
  lang: 'en' | 'it' = 'en';

  @HostBinding('class.dark') isDark = false;

  constructor(
    private translate: TranslateService,
    private inBrowserModel: InBrowserModelService
  ) {
    if (typeof window !== 'undefined') {
      window.addEventListener('click', this.globalRippleHandler.bind(this), true);
    }
  }

  ngOnInit(): void {
    // language: default English unless user preference present
    const savedLang = localStorage.getItem('lang') as 'en' | 'it' | null;
    if (savedLang) this.lang = savedLang;
    else {
      const nav = navigator.language || (navigator as any).userLanguage || 'en';
      this.lang = nav && nav.startsWith('it') ? 'it' : 'en';
    }
    document.documentElement.lang = this.lang;
    this.translate.setLang(this.lang);

    const saved = localStorage.getItem('theme');
    if (saved) {
      this.isDark = saved === 'dark';
    } else {
      this.isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    this.applyClass();

    // Initialize in-browser LLM model globally (will be cached and ready for use in any component)
    this.initializeInBrowserModel();
    
    // Preload 3D character models in parallel (both home assistant and demo assistant_fm)
    this.preloadCharacterModels();
  }
  
  private preloadCharacterModels(): void {
    // Start preloading both character models in parallel for faster loading
    Promise.all([
      this.preloadModel('assets/personaggi/glb/assistente.glb', 'home'),
      this.preloadModel('assets/personaggi/glb/assistente_fm.glb', 'demo')
    ])
      .then(() => console.log('[AppComponent] All character models preloaded'))
      .catch((e) => console.warn('[AppComponent] Some character models failed to preload:', e));
  }
  
  private preloadModel(url: string, context: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'arraybuffer';
      xhr.onload = () => {
        if (xhr.status === 200) {
          // Cache the model data in sessionStorage or memory for faster access
          console.log(`[AppComponent] Preloaded ${context} model: ${url} (${(xhr.response.byteLength / 1024 / 1024).toFixed(2)} MB)`);
          // Store in sessionStorage for available
          try {
            sessionStorage.setItem(`model_cache_${context}`, new Blob([xhr.response]).size.toString());
          } catch (e) { /* storage full or not available */ }
          resolve();
        } else {
          reject(new Error(`Failed to preload ${url}: ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new Error(`Network error loading ${url}`));
      xhr.onprogress = (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          // console.log(`[AppComponent] Preloading ${context}: ${percentComplete.toFixed(0)}%`);
        }
      };
      xhr.open('GET', url, true);
      xhr.send();
    });
  }

  private initializeInBrowserModel(): void {
    (async () => {
      try {
        console.log('[AppComponent] Starting in-browser model initialization...');
        await this.inBrowserModel.init((msg: string) => console.log('[in-browser-model]', msg));
        console.log('[AppComponent] In-browser model ready and cached globally');
      } catch (e) {
        console.warn('[AppComponent] In-browser model init failed (will retry on demand):', e);
      }
    })();
  }

  setLang(l: 'en'|'it') {
    console.debug('[AppComponent] setLang', l);
    this.lang = l;
    document.documentElement.lang = l;
    localStorage.setItem('lang', l);
    this.translate.setLang(l);
  }

  toggleTheme(): void {
    this.isDark = !this.isDark;
    localStorage.setItem('theme', this.isDark ? 'dark' : 'light');
    this.applyClass();
  }

  private applyClass() {
    const root = document.documentElement;
    if (this.isDark) root.classList.add('dark'); else root.classList.remove('dark');
  }

  // Ripple effect: attach a delegated click listener to create ripples on ion-button clicks
  private globalRippleHandler(ev: MouseEvent) {
    const target = ev.target as HTMLElement | null;
    if (!target) return;

    const btn = target.closest('ion-button, button, .ripple') as HTMLElement | null;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const ripple = document.createElement('span');
    ripple.className = 'ripple-effect';
    const size = Math.max(rect.width, rect.height) * 1.6;
    ripple.style.width = ripple.style.height = size + 'px';
    const x = ev.clientX - rect.left - size / 2;
    const y = ev.clientY - rect.top - size / 2;
    ripple.style.left = x + 'px';
    ripple.style.top = y + 'px';
    btn.style.position = btn.style.position || 'relative';
    btn.appendChild(ripple);

    window.setTimeout(() => {
      ripple.classList.add('ripple-fade');
      window.setTimeout(() => {
        ripple.remove();
      }, 400);
    }, 200);
  }
}